/**
 * Patient cards: the compact list card and the full sheet with its action dock.
 * Cards look like the printed ones: name band, provisional, flavour, certainty track.
 */
import { useEffect, useState, type ReactElement } from 'react';
import type { Affordance, PatientView } from '@nightshift/engine/client';
import { CertaintyTrack, ClockRow, Fold, ROLE_TITLE, Sheet, TriageBadge } from '../ui/bits.tsx';

const LINE_NAMES: Record<string, string> = {
  ASSESS: 'Bedside Assess',
  OPINION_PHYS: 'Physician opinion',
  OPINION_PSYCH: 'Psychiatry opinion',
  OPINION_SURG: 'Surgical opinion',
  SCAN: 'Scan',
  TRUTH: 'Truth',
  COMPLICATION: 'Complication',
  EARLY: 'Early',
  BIAS: 'Bias',
};

/** Something that happened to a patient this turn, shown as a badge on the card. */
export type Change = 'new' | 'worse' | 'referred' | 'line' | 'delayed';
const CHANGE_LABEL: Record<Change, string> = { new: 'New', worse: 'Worse', referred: 'Referred in', line: 'New result', delayed: 'Extra stay' };
const CHANGE_ORDER: Change[] = ['worse', 'delayed', 'new', 'referred', 'line'];

function nextStepLabel(p: PatientView): string {
  if (p.needsStabilise) return 'Needs stabilising';
  if (!p.route) return 'No route yet';
  const next = p.route.steps.find((s) => s.state === 'next');
  return next ? `${p.route.code} route, next: ${next.label}` : 'Route complete';
}

export function PatientCardCompact({ p, onOpen, here, changes }: { p: PatientView; onOpen: () => void; here?: string; changes?: Change[] }) {
  const urgent = p.ticks >= p.clockLen - 1;
  const left = p.clockLen - p.ticks;
  const marks = CHANGE_ORDER.filter((c) => changes?.includes(c));
  return (
    <button className={`pcard ${p.colour} ${urgent ? 'urgent' : ''} ${marks.includes('worse') ? 'worse' : ''}`} onClick={onOpen} data-testid={`patient-${p.id}`}>
      {marks.length > 0 && (
        <span className="pcard-changes" data-testid={`changes-${p.id}`}>
          {marks.map((c) => (
            <em key={c} className={`chg chg-${c}`}>
              {CHANGE_LABEL[c]}
            </em>
          ))}
        </span>
      )}
      <span className="pcard-band">
        <TriageBadge colour={p.colour} />
        <span className="pcard-name">{p.name}</span>
        <span className="pcard-where">{here ?? p.where}</span>
      </span>
      <span className="pcard-diag">
        <span className="pcard-dx">{p.believed}</span>
        {p.flipped && <span className="pcard-was">was {p.provisional}</span>}
      </span>
      <span className="pcard-meta">
        <span className={`cert-chip ${p.box === 100 ? 'ok' : ''}`}>{p.box === 100 ? 'Confirmed' : `${p.box}% sure`}</span>
        <span className="pcard-clock">
          <ClockRow ticks={p.ticks} len={p.clockLen} />
          <span className={`pcard-left ${left <= 1 ? 'bad' : ''}`}>{left} left</span>
        </span>
      </span>
      <span className="pcard-next">
        {nextStepLabel(p)}
        {p.flags.referredBy && <em className="tag">Referred by {ROLE_TITLE[p.flags.referredBy]}</em>}
        {p.flags.divertedFrom && <em className="tag">Diverted from Table {p.flags.divertedFrom}</em>}
        {p.flags.bouncedBack && <em className="tag bad">Bounced back</em>}
        {p.flags.waitingICU && <em className="tag bad">Waiting for ICU</em>}
        {p.flags.unstaffed && <em className="tag bad">Unstaffed</em>}
        {p.flags.overflowDelayed && !marks.includes('delayed') && <em className="tag">Extra stay this shift</em>}
        {p.flags.decree && <em className="tag">Consultant’s decree</em>}
        {p.flags.supported && <em className="tag ok">Supported</em>}
      </span>
    </button>
  );
}

/**
 * The one action most likely wanted next: the running route's step, else
 * stabilising, else finding out (if not confirmed), else starting a route.
 */
function primaryAction(p: PatientView): Affordance | null {
  const on = p.actions.filter((a) => a.enabled);
  const pick = (types: string[]) => on.find((a) => types.includes(a.cmd.type));
  return (
    pick(['STABILISE']) ??
    pick(['TREAT', 'OPERATE', 'SUPPORT', 'STEP_DOWN', 'DISCHARGE']) ??
    (p.box !== 100 ? pick(['ASSESS', 'OPINION', 'SCAN']) : null) ??
    (p.route ? null : pick(['START_ROUTE'])) ??
    null
  );
}

function ActionButton({ a, onAct, busy, testId, className }: { a: Affordance; onAct: (a: Affordance) => void; busy: boolean; testId?: string; className?: string }) {
  return (
    <button
      className={`act ${a.enabled ? '' : 'off'} ${a.danger ? 'danger' : ''} grp-${a.group} ${className ?? ''}`}
      disabled={!a.enabled || busy}
      onClick={() => onAct(a)}
      data-testid={testId ?? `act-${a.id}`}
      title={a.reason}
    >
      <span className="act-top">
        <span className="act-label">{a.label}</span>
        {a.cost > 0 && <span className="act-cost">{a.cost} action{a.cost > 1 ? 's' : ''}</span>}
        {a.cost === 0 && a.enabled && <span className="act-free">free</span>}
      </span>
      {a.bet && <span className={`act-bet ${a.risk ? `risk-${a.risk}` : ''}`}>{a.bet}</span>}
      {!a.enabled && a.reason && <span className="act-why">{a.reason}</span>}
    </button>
  );
}

const GROUPS: { key: Affordance['group'][]; title: string }[] = [
  { key: ['certainty'], title: 'Find out' },
  { key: ['treat'], title: 'Treat' },
  { key: ['route'], title: 'Choose a route' },
  { key: ['move'], title: 'Move' },
  { key: ['help', 'layer'], title: 'Ask for help' },
];

/** Keep observed clinical facts separate from the saved care proposal. */
function PatientDetails({ p }: { p: PatientView }): ReactElement {
  return (
      <article className={`card-full ${p.colour}`}>
        <header className="cf-band">
          <TriageBadge colour={p.colour} size={36} />
          <div>
            <h2 className="cf-name">{p.name}</h2>
            <p className="cf-where">
              {p.where}
              {p.holder ? `, ${ROLE_TITLE[p.holder]}` : ''}
            </p>
          </div>
          <div className="cf-clock">
            <span className="cf-clock-n display">{p.clockLen - p.ticks}</span>
            <span className="cf-clock-l">waiting steps left</span>
          </div>
        </header>
        <section className="cf-prov">
          <span className="cf-label">{p.flipped ? 'Now known to be' : 'Provisional'}</span>
          <p className="cf-dx display">{p.believed}</p>
          {p.flipped && <p className="cf-was">Arrived as {p.provisional}</p>}
        </section>
        <Fold className="on-paper cf-about" title="About this patient" hint={!p.flipped && p.differentials.length > 0 ? `${p.differentials.length} differential${p.differentials.length > 1 ? 's' : ''}` : undefined} testId="about-patient">
          {!p.flipped && p.differentials.length > 0 && (
            <p className="cf-ddx">
              Could be {p.differentials.map((d) => (p.ruledOut.includes(d) ? `${d} (ruled out)` : d)).join(' or ')}. Compendium page {p.page}.
            </p>
          )}
          <p className="cf-flavour">{p.flavour}</p>
        </Fold>
        <section className="cf-track">
          <span className="cf-label">Certainty</span>
          <CertaintyTrack box={p.box} printed={p.printedBox} />
          <p className="cf-hint">{certaintyHint(p)}</p>
          <span className="cf-label" style={{ marginTop: 10 }}>Waiting time</span>
          <ClockRow ticks={p.ticks} len={p.clockLen} />
        </section>
        <PatientHistory p={p} />
        {p.planned.route && (p.planned.route.code !== p.route?.code || p.planned.route.outstanding !== p.route?.outstanding) && <p className="cf-hint">Planned care: {p.planned.route.label} · {p.planned.route.outstanding} steps left after the care you have planned. The card itself changes only when the turn ends.</p>}
        {p.needsStabilise && !p.planned.needsStabilise && <p className="cf-hint">Stabilisation is planned; it has not happened yet.</p>}
      </article>
  );
}

/** Show only investigation results and care already released to this player. */
function PatientHistory({ p }: { p: PatientView }): ReactElement {
  return <>
        {p.revealed.length > 0 && (
          <section className="cf-lines">
            <span className="cf-label">What you have read</span>
            <table>
              <tbody>
                {p.revealed.map((r, i) => (
                  <tr key={i} className={r.line === 'BIAS' ? 'bias' : r.text.includes('flip') ? 'flip' : ''}>
                    <th>{LINE_NAMES[r.line] ?? r.line}</th>
                    <td>{r.text}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
        {p.route && (
          <section className="cf-route">
            <span className="cf-label">{p.route.label}</span>
            <ol className="rail">
              {p.route.steps.map((s, i) => (
                <li key={i} className={s.state}>
                  <span className="mono">{s.label}</span>
                  <span className="rail-by">
                    {s.by}
                    {s.warn ? ', ⚠' : ''}
                  </span>
                </li>
              ))}
            </ol>
          </section>
        )}
  </>;
}

/** Explain how patient choices consume the proposed budget when committed. */
function ActionDock({ p, busy, run, actionsLeft }: { p: PatientView; busy: boolean; run: (a: Affordance) => void; actionsLeft: number | null }): ReactElement {
  return (
      <div className="dock">
        <p className="muted">Care and referrals are added to your plan. Sharing a card happens straight away; test results arrive when the turn ends.</p>
        {actionsLeft !== null && (
          <p className={`dock-budget ${actionsLeft === 0 ? 'none' : ''}`} data-testid="actions-left">
            {actionsLeft === 0 ? 'No actions left to plan. You can still change your plan or add free actions.' : `${actionsLeft} action${actionsLeft === 1 ? '' : 's'} left to plan.`}
          </p>
        )}
        {GROUPS.map((g) => {
          const list = p.actions.filter((a) => g.key.includes(a.group));
          if (list.length === 0) return null;
          const buttons = (
            <div className="dock-list">
              {list.map((a) => (
                <ActionButton key={a.id} a={a} onAct={run} busy={busy} />
              ))}
            </div>
          );
          // Essential groups stay open; route options once a route is running, and help, fold away.
          const folded = g.title === 'Ask for help' || (g.title === 'Choose a route' && !!p.route) || (g.title === 'Find out' && p.box === 100);
          if (folded)
            return (
              <Fold key={g.title} title={g.title === 'Choose a route' ? 'Change route' : g.title} hint={g.title === 'Find out' ? 'already confirmed' : `${list.length} option${list.length > 1 ? 's' : ''}`} testId={`fold-${g.key[0]}`}>
                {buttons}
              </Fold>
            );
          return (
            <section key={g.title} className="dock-group">
              <h3 className="dock-title">{g.title}</h3>
              {buttons}
            </section>
          );
        })}
        {p.actions.length === 0 && <p className="muted">Nothing you can do for this patient right now.</p>}
      </div>
  );
}

/** Confirm the clinical risk before saving an empirical choice. */
function CriticalConfirm({ p, onConfirm, onCancel }: { p: PatientView; onConfirm: () => void; onCancel: () => void }): ReactElement {
  return <div className="confirm" role="alertdialog" aria-label="Confirm a risky treatment">
    <p><strong>{p.box === 100 ? 'Confirmed' : `${p.box}% sure`}.</strong> If the diagnosis turns out to be wrong, {p.name.split(' ')[0]} dies. Add this empirical treatment to your plan?</p>
    <div className="row">
      <button className="btn btn-danger" onClick={onConfirm} data-testid="confirm-yes">Add to plan</button>
      <button className="btn btn-quiet" onClick={onCancel}>Not yet</button>
    </div>
  </div>;
}

export function PatientSheet({ p, open, onClose, act, actionsLeft = null, readOnly = false }: { p: PatientView | null; open: boolean; onClose: () => void; act: (a: Affordance) => Promise<boolean>; actionsLeft?: number | null; readOnly?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<Affordance | null>(null);
  useEffect(() => setConfirm(null), [p?.id, readOnly]);
  if (!p) return null;
  const primary = actionsLeft === 0 || readOnly ? null : primaryAction(p);
  const run = async (a: Affordance) => {
    if (readOnly || busy) return;
    if (a.danger && !confirm) { setConfirm(a); return; }
    setConfirm(null);
    setBusy(true);
    try {
      const ok = await act(a);
      if (ok && ['REFER', 'DISCHARGE', 'EARLY_DISCHARGE', 'STEP_DOWN'].includes(a.cmd.type)) onClose();
    } finally { setBusy(false); }
  };
  return <Sheet open={open} onClose={onClose} label={`${p.name}, ${p.believed}`}>
    <PatientDetails p={p} />
    {confirm && !readOnly && <CriticalConfirm p={p} onConfirm={() => run(confirm)} onCancel={() => setConfirm(null)} />}
    {readOnly && <p className="phase-note">You cannot change this patient’s care right now: your plan is saving, needs checking, or it is not the Action phase.</p>}
    <ActionDock p={p} busy={busy || readOnly} run={run} actionsLeft={actionsLeft} />
    {primary && <div className="dock-primary"><span className="dock-primary-label">Plan next</span><ActionButton a={primary} onAct={run} busy={busy} testId="primary-action" className="primary" /></div>}
  </Sheet>;
}

/** One line under the certainty track explaining its two marks. */
function certaintyHint(p: PatientView): string {
  const pct = (b: number) => (b === 100 ? 'confirmed' : `${b}%`);
  if (p.box === 100) return 'Confirmed: the paperclip is on the ✓.';
  return `Paperclip: how sure you are now (${pct(p.box)}). Dot: how sure the card was on arrival${p.printedBox === p.box ? '' : ` (${pct(p.printedBox)})`}. Bedside assessment, opinions and scans move the paperclip.`;
}
